import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { OrganizationService } from "@services";
import { ORGANIZATION_TYPES } from "@constants";
import utils from "@utils";
import MigrateModal from "./components/MigrateModal";
import { ConfirmationModal, SearchField, ScrollDetect } from "../../../components";

import "./OrganizationList.scss";

const OrganizationsList = ({ router, user }) => {
	const {
		location: { query },
	} = router;
	const { lang } = global;
	const displayCount = 25;
	const [organizations, setOrganizations] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);

	const [filters, setFilters] = useState({
		sortBy: query.sortBy || "name",
		asc: query.asc === "true" || true,
		name: query.name || "",
		type: query.type || "",
	});

	const _confirmationModal = useRef();
	const _reassignModal = useRef();

	/** A callbacl method used to retrieve a list of organizations that exist in the agency portal */
	const getOrganizations = useCallback(() => {
		// Remove empty query parameters for the API request
		const queryParams = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);
		OrganizationService.getAllDetailed({ ...queryParams, pageNumber, displayCount })
			.then(({ data }) => {
				const newData = data.ItemList ?? [];
				setOrganizations(pageNumber > 1 ? [...organizations, ...newData] : newData);
				setPageNumber(pageNumber + 1);
				setHasMore(newData.length >= displayCount);
			})
			.catch(({ response: { data } }) => {
				setOrganizations([]);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading organizations"),
					title: "Error",
				});
			});
	});

	const onFilter = (property, value) => {
		// Only update the state if the incoming value is different than the current value
		if (value !== filters[property]) {
			setFilters(prevFilters => ({ ...prevFilters, [property]: value ?? "" }));
		}
	};

	/**
	 * Deletes or Migrates users of the selected organization and removes it from the list
	 * @param {Number} id The unique id of the organization
	 * @param {Number} replacementId The unique id of a different organization if wanting to migrate users to a different organization
	 */
	const deleteOrganization = (id, replacementId = undefined) => {
		OrganizationService.delete(id, replacementId)
			.then(({ data }) => {
				setOrganizations(organizations.filter(org => org.Id !== id));
				let message = `${data?.Message ?? lang.t("Organization deleted successfully")}`;
				message += replacementId ? ` ${lang.t("Users migrated")}` : "";
				utils.notify({ type: "success", message });
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error deleting organization"),
					timeOut: 1500,
				});
			});
	};

	const onTableSort = property => () => {
		if (property === filters.sortBy) {
			onFilter("asc", !filters.asc);
		} else {
			onFilter("sortBy", property);
		}
	};

	/** Displays a confirmation modal prompting the user to confirm their actions before deleting an organization */
	const onDelete = (organizationId, users) => {
		if (users === 0) {
			_confirmationModal.current.openModal({
				title: lang.t("Delete Organization"),
				message: `${lang.t("Are you sure you want to delete organization")} ${organizationId}?`,
				onConfirm: () => deleteOrganization(organizationId),
			});
		} else {
			_reassignModal.current.openModal({
				organizationId,
				users,
			});
		}
	};

	useEffect(() => {
		// If the page is reached via the user dropdown menu, the query string is reset.
		// When this happens, the page state should also update to reflect this change
		if (
			Object.keys(query).length === 0 &&
			(!filters.asc ||
				filters.sortBy !== "name" ||
				filters.name !== "" ||
				filters.type !== "")
		) {
			setFilters({
				sortBy: "name",
				asc: true,
				name: "",
				type: "",
			});
		}
	}, [query]);

	// Reset visibility of the scrolldetect element after it has become visible once
	useEffect(() => {
		if (visible) {
			getOrganizations();
			setVisible(false);
		}
	}, [visible]);

	useEffect(() => {
		const params = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);
		router.push({ pathname: "/organizations", query: params });
		setPageNumber(1);
		setOrganizations([]);
		setHasMore(true);
	}, [filters.sortBy, filters.asc, filters.name, filters.type]);

	return (
		<div
			id="organization-list"
			className="container"
		>
			<h2>
				{lang.t("Organizations")}
				<If condition={filters.type !== ""}>
					&nbsp;/{" "}
					{ORGANIZATION_TYPES.find(({ value }) => value.toString() === filters.type)
						?.label || ""}
				</If>
			</h2>
			<div className="row filters">
				<div className="col-sm-4 col-md-3">
					<span className="invisible placeholder">Name</span>
					<SearchField
						placeholder={lang.t("Search by Name")}
						onSearch={value => onFilter("name", value)}
						clearable
						defaultValue={filters.name}
					/>
				</div>
				<div className="col-sm-4 col-md-3 form-group">
					<label htmlFor="org-type">{lang.t("Type")}</label>
					<select
						value={filters.type}
						name="org-type"
						id="org-type"
						className="form-control"
						onChange={({ target: { value } }) => onFilter("type", value)}
					>
						<option value="">All</option>
						<For
							each="type"
							of={ORGANIZATION_TYPES}
						>
							<option
								key={`org-type_${type.value}`}
								value={type.value}
							>
								{type.label}
							</option>
						</For>
					</select>
				</div>
			</div>
			<div className="row column-headers hidden-xs">
				<div
					role="button"
					tabIndex="0"
					onClick={onTableSort("id")}
					onKeyPress={({ key }) => {
						if (key === "Enter") onTableSort("id");
					}}
					className="col-sm-2"
				>
					<strong>{lang.t("ID")}</strong>
					<If condition={filters.sortBy === "id"}>
						<i
							className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
						/>
					</If>
				</div>
				<div
					role="button"
					tabIndex="0"
					onClick={onTableSort("name")}
					onKeyPress={({ key }) => {
						if (key === "Enter") onTableSort("name");
					}}
					className="col-sm-3"
				>
					<strong>{lang.t("Name")}</strong>
					<If condition={filters.sortBy === "name"}>
						<i
							className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
						/>
					</If>
				</div>
				<div
					role="button"
					tabIndex="0"
					onClick={onTableSort("type")}
					onKeyPress={({ key }) => {
						if (key === "Enter") onTableSort("type");
					}}
					className="col-sm-2"
				>
					<strong>{lang.t("Type")}</strong>
					<If condition={filters.sortBy === "type"}>
						<i
							className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
						/>
					</If>
				</div>
				<div className="col-sm-1">
					<strong>{lang.t("Users")}</strong>
				</div>
				<div
					role="button"
					tabIndex="0"
					onClick={onTableSort("created")}
					onKeyPress={({ key }) => {
						if (key === "Enter") onTableSort("created");
					}}
					className="col-sm-2"
				>
					<strong>{lang.t("Created Date")}</strong>
					<If condition={filters.sortBy === "created"}>
						<i
							className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
						/>
					</If>
				</div>
				<div className="col-sm-2" />
			</div>
			<div className="list-body">
				<For
					each="org"
					of={organizations}
				>
					<Link
						to={`/organizations/${org.Id}`}
						key={`organization_org_${org.Id}`}
					>
						<div className="row">
							<div className="col-xs-3 col-xs-push-9 col-sm-push-0 col-sm-2 org-id text-right">
								{org.Id}
							</div>
							<div
								className="col-xs-9 col-xs-pull-3 col-sm-pull-0 col-sm-3"
								title={org.Name}
							>
								<strong>{org.Name}</strong>
							</div>
							<div className="col-xs-12 col-sm-2">{org.TypeDescription}</div>
							<div className="col-sm-1">
								{org.NumberOfUsers}
								<span className="visible-xs-inline">
									{org.NumberOfUsers === 1 ? " user" : " users"}
								</span>
							</div>
							<div className="col-sm-2">
								<span className="visible-xs-inline">Created&nbsp;</span>
								{utils.formatDate(`${org.Created}Z`, "en-US", user.TimeZoneId)}
							</div>
							<div className="col-sm-2">
								<button
									type="button"
									onClick={e => {
										e.preventDefault();
										onDelete(org.Id, org.NumberOfUsers);
									}}
									className="btn btn-primary btn-block"
								>
									{lang.t("Delete")}
								</button>
							</div>
						</div>
					</Link>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>
			<ConfirmationModal ref={_confirmationModal} />
			<MigrateModal
				ref={_reassignModal}
				onConfirm={deleteOrganization}
			/>
		</div>
	);
};

OrganizationsList.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
};

OrganizationsList.defaultProps = {
	user: {},
};

export default OrganizationsList;
